/**
 * The media picker rail — browse the library and drop existing assets into the
 * post without leaving the composer.
 *
 * Collapsible because it is a *secondary* surface: most posts reuse nothing, and
 * a permanently open file tree steals width from the editor and the preview,
 * which are what the user actually came here for. Collapsed it keeps a rail with
 * the count, so it is still discoverable.
 *
 * Files already attached render as selected and toggle off on click, so this and
 * the Media section never disagree about what is on the post.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ChevronRight,
  FolderClosed,
  FolderOpen,
  Image as ImageIcon,
  Library,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Video,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import {
  DEFAULT_EXPANDED_FOLDER_IDS,
  fetchMediaTree,
} from '../../../api/mediaTree.api';
import type { MediaFolder, MediaLibraryFile } from '../../../api/mediaTree.api';

interface MediaLibraryTreeProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** IDs of library files already on the post. */
  attachedIds: string[];
  onToggleFile: (file: MediaLibraryFile) => void;
}

export function MediaLibraryTree({
  collapsed,
  onToggleCollapsed,
  attachedIds,
  onToggleFile,
}: Readonly<MediaLibraryTreeProps>) {
  const { t } = useTranslation();
  const [tree, setTree] = useState<MediaFolder | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string[]>(DEFAULT_EXPANDED_FOLDER_IDS);

  useEffect(() => {
    let cancelled = false;
    void fetchMediaTree()
      .then((result) => {
        if (!cancelled) setTree(result);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Searching a collapsed tree finds nothing the user can see, so a query opens
  // every folder and closing the search restores the default set.
  const visible = useMemo(() => (tree ? filterTree(tree, query.trim()) : null), [tree, query]);
  const searching = query.trim().length > 0;

  const toggleFolder = (id: string) =>
    setExpanded((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));

  const totalFiles = tree ? countFiles(tree) : 0;

  if (collapsed) {
    return (
      <aside className="flex w-full shrink-0 flex-row items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 lg:h-full lg:w-12 lg:flex-col lg:px-0 lg:py-3">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={t('composer.library.expand')}
          title={t('composer.library.expand')}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-blue-600"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <Library className="h-4 w-4 text-slate-300" />
        <span className="text-[10px] font-bold tabular-nums text-slate-400">{totalFiles}</span>
      </aside>
    );
  }

  return (
    <aside className="flex w-full shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white lg:h-full lg:w-[258px]">
      <div className="flex items-center gap-2 px-3 pb-2 pt-3">
        <Library className="h-4 w-4 shrink-0 text-blue-600" />
        <h2 className="flex-1 truncate text-[13px] font-bold text-slate-900">
          {t('composer.library.title')}
        </h2>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={t('composer.library.collapse')}
          title={t('composer.library.collapse')}
          className="hidden h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 lg:flex"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('composer.library.search')}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-2.5 text-[12px] text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      <div className="max-h-[340px] flex-1 overflow-y-auto px-2 pb-3 lg:max-h-none lg:min-h-0">
        {loading && (
          <div className="space-y-1.5 px-1">
            {Array.from({ length: 7 }, (_, i) => (
              <div key={i} className="h-7 animate-pulse rounded-md bg-slate-100" />
            ))}
          </div>
        )}

        {!loading && visible === null && (
          <p className="px-2 py-6 text-center text-[11.5px] text-slate-400">
            {t('composer.library.noResults')}
          </p>
        )}

        {!loading && visible !== null && (
          <FolderNode
            folder={visible}
            depth={0}
            expandedIds={expanded}
            forceExpanded={searching}
            onToggleFolder={toggleFolder}
            attachedIds={attachedIds}
            onToggleFile={onToggleFile}
          />
        )}
      </div>

      <p className="border-t border-slate-100 px-3 py-2 text-[10.5px] text-slate-400">
        {t('composer.library.hint')}
      </p>
    </aside>
  );
}

interface FolderNodeProps {
  folder: MediaFolder;
  depth: number;
  expandedIds: string[];
  forceExpanded: boolean;
  onToggleFolder: (id: string) => void;
  attachedIds: string[];
  onToggleFile: (file: MediaLibraryFile) => void;
}

function FolderNode({
  folder,
  depth,
  expandedIds,
  forceExpanded,
  onToggleFolder,
  attachedIds,
  onToggleFile,
}: Readonly<FolderNodeProps>) {
  const isOpen = forceExpanded || expandedIds.includes(folder.id);
  const childCount = folder.files.length + folder.folders.length;

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggleFolder(folder.id)}
        aria-expanded={isOpen}
        style={{ paddingLeft: 6 + depth * 12 }}
        className="flex w-full items-center gap-1.5 rounded-md py-1.5 pr-2 text-left transition-colors hover:bg-slate-50"
      >
        <ChevronRight
          className={cn(
            'h-3 w-3 shrink-0 text-slate-400 transition-transform',
            isOpen && 'rotate-90'
          )}
        />
        {isOpen ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-amber-500" />
        ) : (
          <FolderClosed className="h-3.5 w-3.5 shrink-0 text-amber-500" />
        )}
        <span className="flex-1 truncate text-[12px] font-semibold text-slate-700">
          {folder.name}
        </span>
        <span className="shrink-0 text-[10px] tabular-nums text-slate-400">{childCount}</span>
      </button>

      {isOpen && (
        <div>
          {folder.folders.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              forceExpanded={forceExpanded}
              onToggleFolder={onToggleFolder}
              attachedIds={attachedIds}
              onToggleFile={onToggleFile}
            />
          ))}

          {folder.files.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              depth={depth + 1}
              attached={attachedIds.includes(file.id)}
              onToggle={() => onToggleFile(file)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileRow({
  file,
  depth,
  attached,
  onToggle,
}: Readonly<{
  file: MediaLibraryFile;
  depth: number;
  attached: boolean;
  onToggle: () => void;
}>) {
  const { t } = useTranslation();
  const TypeIcon = file.type === 'video' ? Video : ImageIcon;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={attached}
      title={attached ? t('composer.library.remove') : t('composer.library.add')}
      style={{ paddingLeft: 6 + depth * 12 }}
      className={cn(
        'flex w-full items-center gap-2 rounded-md py-1 pr-2 text-left transition-colors',
        attached ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'
      )}
    >
      <span className="w-3 shrink-0" />
      <img
        src={file.previewUrl}
        alt=""
        className="h-7 w-7 shrink-0 rounded border border-slate-200 object-cover"
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[11.5px] leading-tight',
            attached ? 'font-semibold text-blue-700' : 'text-slate-600'
          )}
        >
          {file.name}
        </span>
        <span className="flex items-center gap-1 text-[10px] leading-tight text-slate-400">
          <TypeIcon className="h-2.5 w-2.5" />
          {file.durationMs !== null
            ? formatDuration(file.durationMs)
            : formatSize(file.sizeBytes)}
        </span>
      </span>
      <span
        aria-hidden
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[11px] font-bold leading-none',
          attached ? 'bg-blue-600 text-white' : 'border border-slate-300 text-slate-400'
        )}
      >
        {attached ? '✓' : '+'}
      </span>
    </button>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Keeps a folder if its own name matches or if anything beneath it does, so a
 * match five levels down is still reachable. Returns null when the whole subtree
 * is filtered out.
 */
function filterTree(folder: MediaFolder, query: string): MediaFolder | null {
  if (query.length === 0) return folder;

  const needle = query.toLowerCase();
  const selfMatches = folder.name.toLowerCase().includes(needle);

  const files = folder.files.filter((f) => f.name.toLowerCase().includes(needle));
  const folders = folder.folders
    .map((child) => filterTree(child, query))
    .filter((child): child is MediaFolder => child !== null);

  if (selfMatches) return folder;
  if (files.length === 0 && folders.length === 0) return null;

  return { ...folder, files, folders };
}

function countFiles(folder: MediaFolder): number {
  return folder.files.length + folder.folders.reduce((sum, f) => sum + countFiles(f), 0);
}

function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}
